import { createContext, useContext, type ReactNode } from "react";
import { Box } from "@mui/material";
import { DesignSurface } from "./DesignSurface.js";
import { useInterfaceDesign } from "./InterfaceDesign.js";
import { designTokens } from "./tokens.js";

const AdminPresentation = createContext(false);
export const useAdminDesign = () => useContext(AdminPresentation);

/** Administration chrome only. Authored previews are isolated by their renderer. */
export function AdminDesignSurface({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  const { mode, colorMode } = useInterfaceDesign();
  const modern = enabled && mode === "modern";
  const p = designTokens[colorMode];
  return (
    <AdminPresentation.Provider value={modern}>
      <DesignSurface enabled={enabled}>
        <Box
          data-admin-design={modern ? "modern" : undefined}
          sx={
            modern
              ? {
                  display: "contents",
                  color: p.text,
                  "& [data-workspace-page] .MuiCard-root:not([data-original-design-surface] *):not([data-planning-workspace] *)":
                    {
                      color: p.text,
                      backgroundColor: p.surface,
                      backgroundImage: "none",
                      border: `1px solid ${p.border}`,
                      borderRadius: "10px",
                      boxShadow: "none",
                    },
                  "& [data-workspace-page] .MuiCardContent-root:not([data-original-design-surface] *):not([data-planning-workspace] *)":
                    { padding: "20px" },
                  "& [data-workspace-page] .MuiCardContent-root:last-child:not([data-original-design-surface] *):not([data-planning-workspace] *)":
                    { paddingBottom: "20px" },
                  "& [data-workspace-page] .MuiTableContainer-root:not([data-original-design-surface] *)":
                    { border: `1px solid ${p.border}`, borderRadius: "8px" },
                  "& [data-workspace-page] .MuiTableCell-root:not([data-original-design-surface] *)":
                    { padding: "10px 14px" },
                  "& [data-workspace-page] > *": { minWidth: 0 },
                  "& [data-workspace-immersive] > *": { flex: 1, minHeight: 0 },

                  "& :is([data-admin-page], [data-workspace-page])": {
                    color: p.text,
                    background: p.background,
                  },
                  "& :is([data-admin-page], [data-workspace-page]) > .MuiStack-root, & :is([data-admin-page], [data-workspace-page]) > .MuiBox-root":
                    { minWidth: 0 },
                  "& :is([data-admin-page], [data-workspace-page]) .MuiTypography-h4:not([data-original-design-surface] *), & :is([data-admin-page], [data-workspace-page]) .MuiTypography-h5:not([data-original-design-surface] *)":
                    { fontSize: 20, fontWeight: 600, letterSpacing: "-.4px" },
                  "& :is([data-admin-page], [data-workspace-page]) .MuiTableHead-root .MuiTableCell-root:not([data-original-design-surface] *)":
                    {
                      background: p.chrome,
                      color: p.muted,
                      fontSize: 10,
                      fontWeight: 500,
                    },
                  "& :is([data-admin-page], [data-workspace-page]) .MuiTableCell-root:not([data-original-design-surface] *)":
                    {
                      borderColor: p.fine,
                      fontSize: 12,
                    },
                  "& :is([data-admin-page], [data-workspace-page]) .MuiTableRow-root:hover:not([data-original-design-surface] *)":
                    {
                      background: p.fill,
                    },
                  "& :is([data-admin-page], [data-workspace-page]) .MuiPaper-outlined:not([data-original-design-surface] *)":
                    {
                      borderColor: p.border,
                      boxShadow: "none",
                    },
                }
              : { display: "contents" }
          }
        >
          {children}
        </Box>
      </DesignSurface>
    </AdminPresentation.Provider>
  );
}
